import { css } from '@emotion/react';
import { ScheduleException } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/exception.pb';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { i18next, useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Chip, styles, Text } from '@frontend/design-system';

dayjs.extend(customParseFormat);

const formatExceptionTimeRange = (exception: ScheduleException) => {
  const startTime = exception.nextOccurrenceStart?.slice(11, 16) || exception.time?.startTime;
  const endTime = exception.time?.endTime;

  if (!startTime || !endTime) return i18next.t('All day', { ns: 'schedule' });

  return `${dayjs(startTime, 'HH:mm').format('h:mm A')} - ${dayjs(endTime, 'HH:mm').format('h:mm A')}`;
};

export const MultiExceptionTooltip = ({ exceptions }: { exceptions: ScheduleException[] }) => {
  const { t } = useTranslation('schedule');
  return (
    <dl css={containerStyle}>
      <Text size='small' color='subdued'>
        {t('{{count}} Calendar Exceptions:', { count: exceptions.length })}
      </Text>
      {exceptions.map((exception, index) => (
        <div key={`${exception.id ?? exception.name ?? 'exception'}-${index}`} css={exceptionRowStyle}>
          <Text as='dt' size='small' weight='bold' color='light' css={styles.truncate}>
            {exception.name}
          </Text>
          <Chip as='dd' size='tiny' variant='neutral' css={{ maxWidth: 'max-content' }}>
            {formatExceptionTimeRange(exception)}
          </Chip>
        </div>
      ))}
    </dl>
  );
};

const containerStyle = css({
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  margin: 0,
  maxWidth: '100%',
  overflow: 'hidden',
  whiteSpace: 'normal',
});

const exceptionRowStyle = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
});
